import React, { useState } from 'react';
import { ChevronDown, ChevronUp, AlertTriangle, Users } from 'lucide-react';
import useGameStore from '../../store/gameStore';
import { lumeryDuoActive, LUMERY_BROTHER_IDS } from '../../data/drivers';
import {
  DriverHeader, TraitsRow, XpBar, GaugesRow, ActionsRow,
  MissionHealth, CareerPanel, TrainingPanel, ExpandedDetails,
  CompetenceLicensesRow, AlertsRow, AssignedVehicleRow,
} from './driverDetail/parts';

/**
 * Detailed driver card — orchestrator.
 *
 * Refactored 29/05/2026 (code review) : the original 565-line monolith
 * (cyclomatic complexity high) was decomposed into 12 focused sub-parts
 * living in `driverDetail/parts.jsx`. This file is now a thin coordinator
 * (~110 lines) that manages the three local states (expanded /
 * showTraining / showCareer) and threads props.
 *
 * Visual output and data-testids are preserved verbatim.
 */
export default function DriverDetailCard({
  driver, vehicles, activeContracts, treasury,
  onRest, onBonus, onFire, onTrain, onUnassign, onSendOnHoliday, onBuyBackHolidays,
  totalDays, t, hrInsightsUnlocked,
}) {
  const [expanded, setExpanded] = useState(false);
  const [showTraining, setShowTraining] = useState(false);
  const [showCareer, setShowCareer] = useState(false);
  // 26/06/2026 — date de retour estimée affichée dans TrainingPanel
  // (recommandation user validée le 26/06/2026).
  const calDay = useGameStore(s => s.day);
  const calMonth = useGameStore(s => s.month);
  const calYear = useGameStore(s => s.year);

  const assignedVehicle = vehicles.find(v => v.assignedDriverId === driver.id);
  // 04/02/2026 — Q1 Bug Messagerie : retrouver l'opportunité commerciale
  // associée pour afficher un statut "Mission flash" distinct dans le
  // header. Avant ce fix, le chauffeur sur opportunité commerciale
  // affichait "Actif" sans précision → user pensait que c'était bloqué
  // sans raison.
  const commercialOpportunities = useGameStore(s => s.commercialOpportunities) || [];
  const commercialOpp = driver.commercialOpportunityId
    ? commercialOpportunities.find(o => o.id === driver.commercialOpportunityId)
    : null;
  const commercialOppWithDaysLeft = commercialOpp
    ? { ...commercialOpp, daysLeft: Math.max(0, (commercialOpp.endsOnDay || 0) - (totalDays || 0)) }
    : null;
  // 14/06/2026 — bonus fratrie Lumery : badge visible quand les deux frères
  // sont au même dépôt (−10 % de fatigue quotidienne chacun).
  const allDrivers = useGameStore(s => s.drivers) || [];
  const lumeryDuo = LUMERY_BROTHER_IDS.includes(driver.dataId) && lumeryDuoActive(allDrivers);
  const fatigueAlert = driver.fatigue > 75;
  const satisfactionAlert = driver.satisfaction < 45;
  const isTraining = driver.status === 'training';
  const trainingDaysLeft = isTraining ? Math.max(0, (driver.trainingEndDay || 0) - totalDays) : 0;
  const isOnMission = !!(assignedVehicle && assignedVehicle.status === 'active');
  const trainingBlocked = isTraining || isOnMission;

  const activeContract = isOnMission && assignedVehicle
    ? (activeContracts || []).find(c => c.vehicleId === assignedVehicle.id)
    : null;
  const totalDuration = activeContract
    ? (activeContract.frequency === 'ponctuel' ? (activeContract.duration || 1) : (activeContract.duration || 30))
    : 0;
  const elapsed = activeContract ? Math.max(0, totalDuration - (activeContract.daysRemaining || 0)) : 0;
  const progressPct = totalDuration > 0 ? Math.min(100, Math.round((elapsed / totalDuration) * 100)) : 0;

  return (
    <div className={`bg-ink-800 rounded-lg border transition-all ${
      isTraining ? 'border-accent/60 bg-accent/10/20' :
      fatigueAlert ? 'border-warn/60' :
      satisfactionAlert ? 'border-danger/40' : 'border-ink-600'
    }`} data-testid={`driver-${driver.id}`}>
      <div className="p-4">
        <div className="flex items-start gap-3">
          <DriverHeader
            driver={driver} isTraining={isTraining}
            trainingDaysLeft={trainingDaysLeft} t={t}
            calDay={calDay} calMonth={calMonth} calYear={calYear}
            commercialOpp={commercialOppWithDaysLeft}
            totalDays={totalDays}
          />
        </div>
        {/* Below-header content (sits in the same right column visually) */}
        <div className="ml-14">
          {lumeryDuo && (
            <div className="mt-1 mb-1 inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-emerald-500/10 border border-emerald-500/40" data-testid={`lumery-duo-badge-${driver.id}`}>
              <Users className="w-3 h-3 text-emerald-300" />
              <span className="text-[10px] font-semibold text-emerald-300">{t('driver_lumery_duo_badge')}</span>
            </div>
          )}
          <TraitsRow
            driver={driver} vehicles={vehicles}
            activeContracts={activeContracts}
            hrInsightsUnlocked={hrInsightsUnlocked}
          />
          <XpBar driver={driver} />
          <CompetenceLicensesRow driver={driver} />
          <GaugesRow driver={driver} t={t} />
          <AlertsRow fatigueAlert={fatigueAlert} satisfactionAlert={satisfactionAlert} t={t} />
          <AssignedVehicleRow
            driver={driver} assignedVehicle={assignedVehicle}
            onUnassign={onUnassign} t={t}
          />
          <ActionsRow
            driver={driver} treasury={treasury}
            isTraining={isTraining} isOnMission={isOnMission}
            trainingBlocked={trainingBlocked}
            onRest={onRest} onBonus={onBonus}
            onSendOnHoliday={onSendOnHoliday}
            onBuyBackHolidays={onBuyBackHolidays}
            setShowTraining={setShowTraining} showTraining={showTraining}
            setShowCareer={setShowCareer} showCareer={showCareer}
            onFire={onFire} t={t}
            calDay={calDay} calMonth={calMonth} calYear={calYear} totalDays={totalDays}
          />
          <MissionHealth
            driver={driver} assignedVehicle={assignedVehicle}
            activeContract={activeContract} totalDuration={totalDuration}
            progressPct={progressPct} t={t}
          />
          {isOnMission && showTraining && (
            <div className="mt-3 border border-warn/40 rounded-lg p-3 bg-warn/5 flex items-start gap-2" data-testid={`training-blocked-${driver.id}`}>
              <AlertTriangle className="w-4 h-4 text-warn flex-shrink-0 mt-0.5" />
              <p className="text-[12px] text-warn leading-snug">{t('cannot_train_on_mission')}</p>
            </div>
          )}
          {showCareer && (
            <CareerPanel
              driver={driver} isOnMission={isOnMission}
              activeContracts={activeContracts}
            />
          )}
          {showTraining && !trainingBlocked && (
            <TrainingPanel
              driver={driver} treasury={treasury}
              onTrain={onTrain} setShowTraining={setShowTraining} t={t}
              calDay={calDay} calMonth={calMonth} calYear={calYear}
            />
          )}
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full mt-2 text-xs text-slate-500 hover:text-slate-400 flex items-center justify-center gap-1 py-0.5 transition-colors"
            data-testid={`toggle-details-${driver.id}`}
          >
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {expanded ? t('hide') : t('details')}
          </button>
        </div>
      </div>
      {expanded && <ExpandedDetails driver={driver} t={t} />}
    </div>
  );
}
